import { isPast } from 'date-fns';
import { FormattedMessage, useIntl } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import type { Sport } from '__generated__/globalTypes';
import { PromotionalEventCompletionState } from '__generated__/globalTypes';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BodyS, BrandS, LabelM, LabelS } from 'atoms/typography';
import type { ConfigQuery } from 'contexts/config/types';
import { useCurrentUserContext } from 'contexts/currentUser';
import { useSportContext } from 'contexts/sport';
import { useAmountWithConversion } from 'hooks/useAmountWithConversion';
import { useFiatBalance } from 'hooks/wallets/useFiatBalance';
import { isType } from 'lib/gql';
import { MonetaryAmount } from 'lib/monetaryAmount';
import { getFormattedSport } from 'lib/sports';

import { ActivePromotionalEventTimeLeft } from '../ActivePromotionalEventTimeLeft';
import { Cta } from '../Cta';
import { useCCRewardRange } from '../Details';
import { Remaining } from '../Remaining';
import { SpentProgress } from '../SpentProgress';

const Root = styled(Vertical).attrs({ center: true })`
  text-align: center;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  color: var(--c-white);
  border: 1px solid;
  background:
    linear-gradient(0deg, var(--c-nd-50), var(--c-nd-50)),
    linear-gradient(0deg, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)),
    var(--bg) center center no-repeat;
  background-size: cover;
  background-position: top;
`;

const StyledLink = styled(Link)`
  color: var(--c-nd-600);
  text-decoration: underline;
`;

const Rules = ({
  probableRewardConfigs,
}: {
  probableRewardConfigs: NonNullable<
    NonNullable<ConfigQuery['currentUser']>['completablePromotionalEvent']
  >['probableRewardConfigs'];
}) => {
  const getCCRewardRange = useCCRewardRange();
  const { currentUser } = useCurrentUserContext();
  const { fiatCurrency } = useFiatBalance();
  const { formatNumber } = useIntl();
  const ccRange = getCCRewardRange(probableRewardConfigs);

  const { max, min } = ccRange || {};

  const { main } = useAmountWithConversion({
    monetaryAmount: min || MonetaryAmount.zero,
  });
  const { main: mainMax } = useAmountWithConversion({
    monetaryAmount: max || MonetaryAmount.zero,
  });
  if (!currentUser?.completablePromotionalEvent) return null;

  if (!ccRange) return null;

  return (
    <FormattedMessage
      id="AddFundsToFiatWallet.Promo.remaining"
      defaultMessage="Deposit {amount}+ for a guaranteed {min}-{max} in market credits!"
      values={{
        amount: formatNumber(
          (currentUser.completablePromotionalEvent.eligibilityRules
            .minimumDepositAmount || 0) / 100,
          {
            style: 'currency',
            currency: fiatCurrency,
            minimumFractionDigits: 0,
          }
        ),
        min: main,
        max: mainMax,
      }}
    />
  );
};

const Title = ({
  completablePromotionalEvent,
  sport,
}: {
  completablePromotionalEvent: NonNullable<
    NonNullable<ConfigQuery['currentUser']>['completablePromotionalEvent']
  >;
  sport: Sport;
}) => {
  const { main } = useAmountWithConversion({
    monetaryAmount:
      completablePromotionalEvent.promotionalEventCompletion?.targetAmount ||
      MonetaryAmount.zero,
  });
  const getCCRewardRange = useCCRewardRange();
  const ccRange = getCCRewardRange(
    completablePromotionalEvent.probableRewardConfigs
  );
  const { main: rewardAmount } = useAmountWithConversion({
    monetaryAmount: ccRange?.min || MonetaryAmount.zero,
  });

  const formattedSport = getFormattedSport(sport);

  if (
    completablePromotionalEvent.completionRules.completion_percentage &&
    completablePromotionalEvent.completionRules.completion_percentage !== 100 &&
    isType(completablePromotionalEvent, 'DepositPromotionalEvent')
  ) {
    return (
      <FormattedMessage
        id="AddFundsToFiatWallet.Promo.compactBanner.inProgress.description2"
        defaultMessage="Spend at least {percentage, number, ::percent} in {sport, select, undefined {} other {{sport} }}Instant Buy or Auctions to get your reward."
        values={{
          percentage:
            completablePromotionalEvent.completionRules.completion_percentage /
            100,
          sport: formattedSport,
        }}
      />
    );
  }
  if (completablePromotionalEvent.promotionalEventCompletion) {
    if (ccRange?.min.isEqualTo(ccRange.max)) {
      return (
        <FormattedMessage
          id="AddFundsToFiatWallet.Promo.compactBanner.inProgress.withFixedConversionCredit.description2"
          defaultMessage="Spend at least {amount} in {sport, select, undefined {} other {{sport} }}Instant Buy or Auctions to get {ccAmount} in market credits."
          values={{
            amount: main,
            ccAmount: rewardAmount,
            sport: formattedSport,
          }}
        />
      );
    }
    return (
      <FormattedMessage
        id="AddFundsToFiatWallet.Promo.compactBanner.inProgress.amount.description"
        defaultMessage="Spend at least {amount} in {sport, select, undefined {} other {{sport} }}Instant Buy or Auctions to get your reward."
        values={{
          amount: main,
          sport: formattedSport,
        }}
      />
    );
  }
  return null;
};

export const CompactBanner = ({
  showDepositCta,
  showInprogress,
}: {
  showInprogress?: boolean;
  showDepositCta?: boolean;
}) => {
  const { currentUser } = useCurrentUserContext();
  const { sport } = useSportContext();
  if (!currentUser) return null;

  const { completablePromotionalEvent } = currentUser;

  if (!completablePromotionalEvent) return null;

  const {
    promotionalEventCompletion,
    sport: completablePromotionalEventSport,
  } = completablePromotionalEvent;

  if (completablePromotionalEventSport !== sport) return null;

  // Case promo is in progress
  if (promotionalEventCompletion) {
    if (
      showInprogress &&
      promotionalEventCompletion.aasmState ===
        PromotionalEventCompletionState.PENDING &&
      !isPast(promotionalEventCompletion.expiresAt)
    ) {
      return (
        <Root
          style={
            {
              '--bg': `url(${completablePromotionalEvent.pictureUrl})`,
            } as CSSProperties
          }
        >
          <Horizontal>
            <ActivePromotionalEventTimeLeft />
          </Horizontal>
          <BodyS>
            <Title
              completablePromotionalEvent={completablePromotionalEvent}
              sport={completablePromotionalEventSport}
            />
          </BodyS>
          <SpentProgress />
        </Root>
      );
    }
    return null;
  }

  if (
    !completablePromotionalEvent ||
    isPast(completablePromotionalEvent.endDate)
  ) {
    return null;
  }

  const hasMaximumNumberOfParticipants =
    isType(completablePromotionalEvent, 'DepositPromotionalEvent') &&
    !!completablePromotionalEvent.completionRules
      .maximum_number_of_participants;

  return (
    <Root
      style={
        {
          '--bg': `url(${completablePromotionalEvent.pictureUrl})`,
        } as CSSProperties
      }
    >
      <BrandS bold>{completablePromotionalEvent.name}</BrandS>
      <LabelM color="var(--c-nd-600)">
        {completablePromotionalEvent.description}
      </LabelM>
      {!!hasMaximumNumberOfParticipants && (
        <Remaining
          completedParticipants={
            completablePromotionalEvent.completedParticipants
          }
          maximumNumberOfParticipants={
            completablePromotionalEvent.completionRules
              .maximum_number_of_participants
          }
        />
      )}
      <LabelS bold>
        {completablePromotionalEvent.marketBannerTitle || (
          <Rules
            probableRewardConfigs={
              completablePromotionalEvent.probableRewardConfigs
            }
          />
        )}{' '}
        <StyledLink
          to={completablePromotionalEvent.blogUrl || ''}
          target="_blank"
          rel="noopener noreferrer"
        >
          <FormattedMessage
            id="AddFundsToFiatWallet.Promo.moreDetails"
            defaultMessage="More details"
          />
        </StyledLink>
      </LabelS>
      {!!showDepositCta && <Cta color="quaternary" />}
    </Root>
  );
};
